import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralize } from "@common/helpers/string";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

export const DocumentBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open, close } = useCommands();
    const {
        selectedDocumentCount = 0,
        documentBulkEntityOptions,
        documentBulkAccountOptions,
        documentBulkCategoryOptions,
        isDocumentBulkFieldLocked,
        documentBulkFieldLockedTooltip,
        onDocumentBulkRemove,
        onDocumentBulkUnmatch,
        onDocumentBulkAddToImporter,
        onDocumentBulkCreateTask,
        onDocumentBulkRevertRename,
    } = options;

    const heading = `${selectedDocumentCount} ${pluralize("document", selectedDocumentCount)} selected`;

    return (
        <Command.Group heading={heading}>
            {onDocumentBulkRemove && (
                <CommandItem
                    keywords={["remove", "delete", "trash"]}
                    onSelect={() => {
                        close();
                        onDocumentBulkRemove();
                    }}
                >
                    Remove
                </CommandItem>
            )}
            {documentBulkEntityOptions &&
                documentBulkEntityOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "entity",
                            "entities",
                            "set entity",
                            "client",
                        ]}
                        disabled={isDocumentBulkFieldLocked}
                        disabledTooltip={documentBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.DOCUMENT_BULK_ENTITY,
                                options,
                            )
                        }
                    >
                        Set entity
                    </CommandItem>
                )}
            {documentBulkAccountOptions &&
                documentBulkAccountOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "account",
                            "set account",
                            "financial account",
                        ]}
                        disabled={isDocumentBulkFieldLocked}
                        disabledTooltip={documentBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.DOCUMENT_BULK_ACCOUNT,
                                options,
                            )
                        }
                    >
                        Set account
                    </CommandItem>
                )}
            {documentBulkCategoryOptions &&
                documentBulkCategoryOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "category",
                            "set category",
                            "document category",
                            "type",
                        ]}
                        onSelect={() =>
                            open(
                                CommandsDirectory.DOCUMENT_BULK_CATEGORY,
                                options,
                            )
                        }
                    >
                        Set category
                    </CommandItem>
                )}
            {onDocumentBulkAddToImporter && (
                <CommandItem
                    keywords={["import", "importer", "add to importer"]}
                    onSelect={() => {
                        close();
                        onDocumentBulkAddToImporter();
                    }}
                >
                    Add to importer
                </CommandItem>
            )}
            {onDocumentBulkUnmatch && (
                <CommandItem
                    keywords={["unmatch", "unlink", "transactions"]}
                    onSelect={() => {
                        close();
                        onDocumentBulkUnmatch();
                    }}
                >
                    Unmatch transactions
                </CommandItem>
            )}
            {onDocumentBulkCreateTask && (
                <CommandItem
                    keywords={["task", "create task"]}
                    onSelect={() => {
                        close();
                        onDocumentBulkCreateTask();
                    }}
                >
                    Create task
                </CommandItem>
            )}
            {onDocumentBulkRevertRename && (
                <CommandItem
                    keywords={[
                        "revert",
                        "rename",
                        "original name",
                        "undo",
                        "restore",
                    ]}
                    onSelect={() => void onDocumentBulkRevertRename()}
                >
                    Restore original name
                </CommandItem>
            )}
        </Command.Group>
    );
};
